import {
  Button,
  Grid2,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ContainerLoading from "../components/ContainerLoading";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useFormik } from "formik";
import { Input } from "../components/Input";
import { fightById } from "../services/fights";
import { useNavigate, useParams } from "react-router";
import ModalityDelete from "../components/ModalityDelete";
import { modalityUpdate } from "../services/modality";
import { toast } from "react-toastify";
import {
  IconArrowDown,
  IconArrowUp,
  IconEdit,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { isColorLight } from "../helpers/format";
import { fightOrderGraduation } from "../services/store";
import GraduationDelete from "../components/GraduationDelete";
import GraduationUpdate from "../components/GraduationUpdate";
import GraduationRegister from "../components/GraduationRegister";
import PageHeader from "../components/PageHeader";
import BoxIcon from "../assets/png/Boxing.png";

const ModalityDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [value, setValue] = useState("1");
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [openGraduation, setOpenGraduation] = useState(false);
  const [openGraduationDelete, setOpenGraduationDelete] = useState(false);
  const [openUpdateGraduation, setOpenUpdateGraduation] = useState(false);
  const [idd, setIdd] = useState<number>();
  const [graduationUpdate, setGraduationUpdate] = useState<{
    id: number;
    color: string;
    belt: string;
    minClass: string;
    minMonthTime: number;
  }>({
    id: 0,
    color: "",
    belt: "",
    minClass: "",
    minMonthTime: 0,
  });

  const onGraduationUpdate = (item: {
    id: number;
    color: string;
    belt: string;
    minClass: string;
    minMonthTime: number;
  }) => {
    setOpenUpdateGraduation(true);
    setGraduationUpdate(item);
  };

  const handleChangeValue = (
    _event: React.SyntheticEvent,
    newValue: string
  ) => {
    setValue(newValue);
  };

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  }: {
    data: any;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  } = useQuery({
    queryKey: ["fightById"],
    queryFn: async () => await fightById(Number(id)),
    initialData: {},
  });

  const { mutate, isPending } = useMutation({
    mutationFn: modalityUpdate,
    onSuccess: () => {
      refetch();
      toast.success("Modalidade atualizada");
    },

    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const { values, handleChange, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      id: "",
      name: "",
      description: "",
    },
    onSubmit: (values) => {
      mutate(values);
    },
  });

  useEffect(() => {
    if (data) {
      setFieldValue("id", data?.id);
      setFieldValue("name", data?.name);
      setFieldValue("description", data?.description);
    }
  }, [data]);

  const [dataGraduations, setDataGraduations] = useState(
    data?.graduations?.map((i: any, index: any) => ({
      ...i,
      key: String(index),
    })) || []
  );

  useEffect(() => {
    setDataGraduations(
      data?.graduations?.map((i: any, index: any) => ({
        ...i,
        key: String(index),
      })) || []
    );
  }, [data?.graduations]);

  const onGraduationDelete = (id: number) => {
    setOpenGraduationDelete(true);
    setIdd(id);
  };

  const { mutate: mutateUpdateOrder, isPending: isPendingUpdateOrder } =
    useMutation({
      mutationFn: async (body: any) => await fightOrderGraduation(body),
      onError: (e: { response: { data: { message: string } } }) => {
        toast.error(String(e?.response?.data?.message) || "Erro!");
      },
    });

  async function onReordered(fromIndex: number, toIndex: number) {
    const copy = [...dataGraduations];
    const removed = copy.splice(fromIndex, 1);
    copy.splice(toIndex, 0, removed[0]);
    mutateUpdateOrder(copy.map((i, index) => ({ id: i.id, order: index })));
    setDataGraduations(copy);
  }

  const loading = isPending || isLoading || isFetching || isPendingUpdateOrder;

  return (
    <ContainerLoading loading={loading}>
      <Grid2 container spacing={2} padding={2}>
        <PageHeader
          title="Detalhes da modalidade"
          subtitle="Informações gerais da modalidade."
          back
        />

        <Grid2 size={12}>
          <Tabs
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              marginBottom: 2,
            }}
            onChange={handleChangeValue}
            value={value}
            textColor="inherit"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Dados da modalidade" value="1" />
            <Tab label="Graduação" value="2" />
          </Tabs>
        </Grid2>
        {value === "1" && (
          <Grid2 size={12}>
            <Grid2 container spacing={2}>
              <Grid2 size={6}>
                <Input
                  id="name"
                  name="name"
                  label="Nome"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.name}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={6}>
                <Input
                  id="description"
                  name="description"
                  label="Descrição"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.description}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={12}>
                <Stack
                  sx={{
                    flexDirection: "row",
                    alignItems: "flex-end",
                    width: "100%",
                    justifyContent: "flex-end",
                    gap: 1,
                  }}
                >
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => setDeleteAlert(true)}
                  >
                    Excluir
                  </Button>
                  <Button variant="contained" onClick={() => handleSubmit()}>
                    Atualizar
                  </Button>
                </Stack>
              </Grid2>
            </Grid2>
          </Grid2>
        )}

        {value === "2" && (
          <>
            <Grid2 size={12}>
              <Button
                sx={{ backgroundColor: "secondary.dark" }}
                fullWidth
                variant="contained"
                size="large"
                onClick={() => setOpenGraduation(true)}
              >
                <IconPlus size={18} />
                <Typography variant="body1" fontWeight={600}>
                  Adicionar graduação
                </Typography>
              </Button>
            </Grid2>

            {dataGraduations?.length > 0 ? (
              <>
                {dataGraduations?.map((i: any) => {
                  const color = isColorLight(i?.color) ? "#000" : "#fff";
                  return (
                    <Grid2 key={i?.id} size={12}>
                      <Stack
                        sx={{
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                          padding: 2,
                          borderRadius: 3,
                          backgroundColor: i.color ? i.color : "secondary.dark",
                          color: color,
                        }}
                      >
                        <Stack
                          sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Stack
                            spacing={1}
                            sx={{
                              flexDirection: "column",
                            }}
                          >
                            <IconButton
                              sx={{
                                backgroundColor: "secondary.dark",
                                width: 30,
                                height: 30,
                              }}
                              onClick={() =>
                                onReordered(
                                  dataGraduations?.indexOf(i),
                                  dataGraduations?.indexOf(i) - 1
                                )
                              }
                            >
                              <IconArrowUp />
                            </IconButton>
                            <IconButton
                              sx={{
                                backgroundColor: "secondary.dark",
                                width: 30,
                                height: 30,
                              }}
                              onClick={() =>
                                onReordered(
                                  dataGraduations?.indexOf(i),
                                  dataGraduations?.indexOf(i) + 1
                                )
                              }
                            >
                              <IconArrowDown />
                            </IconButton>
                          </Stack>
                          <Stack spacing={0.5}>
                            <Typography variant="body1" fontWeight={700}>
                              {i.belt}
                            </Typography>
                            <Typography variant="body1" fontSize={12}>
                              Aulas minimas: {i.minClass}
                            </Typography>
                            <Typography variant="body1" fontSize={12}>
                              Tempo minimo(mês): {i.minMonthTime}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack
                          sx={{
                            flexDirection: "row",
                            gap: 1,
                            alignItems: "center",
                          }}
                        >
                          <IconButton
                            sx={{ backgroundColor: "secondary.dark" }}
                            onClick={() => onGraduationUpdate(i)}
                          >
                            <IconEdit size={18} />
                          </IconButton>
                          <IconButton
                            sx={{ backgroundColor: "secondary.dark" }}
                            onClick={() => onGraduationDelete(i.id)}
                          >
                            <IconTrash size={18} />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Grid2>
                  );
                })}
              </>
            ) : (
              <Grid2 size={12}>
                <Stack sx={{ width: "100%", alignItems: "center" }}>
                  <img width={200} height={200} src={BoxIcon} alt="box-icon" />
                  <Typography
                    fontSize={18}
                    fontWeight={700}
                    color="secondary.contrastText"
                  >
                    Sem graduações aparentes
                  </Typography>
                </Stack>
              </Grid2>
            )}
          </>
        )}
      </Grid2>
      <ModalityDelete
        open={deleteAlert}
        onClose={() => setDeleteAlert(false)}
        id={String(id)}
        onAction={() => navigate("/modality")}
      />
      {openGraduation && (
        <GraduationRegister
          open={openGraduation}
          onClose={() => setOpenGraduation(false)}
          fightsId={String(id)}
          refetch={() => {
            refetch();
          }}
        />
      )}
      {openUpdateGraduation && (
        <GraduationUpdate
          open={openUpdateGraduation}
          onClose={() => setOpenUpdateGraduation(false)}
          data={graduationUpdate}
          refetch={() => {
            refetch();
          }}
        />
      )}
      <GraduationDelete
        open={openGraduationDelete}
        onClose={() => setOpenGraduationDelete(false)}
        id={String(idd)}
        onAction={() => {
          refetch();
        }}
      />
    </ContainerLoading>
  );
};

export default ModalityDetails;
